import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useGenerationStore } from '@/stores/generation';
import { BriefCreatorOverlay } from '@/components/brief/BriefCreatorOverlay';
import {
  BriefSuggestionRail,
  PlaybookLibrary,
  StrategyPerformanceHero,
} from '@/components/strategy';
import { personasApiV2 } from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';

interface ModalDefaults {
  defaultCampaignType?: string;
  defaultClientUid?: string;
  defaultPersonaId?: string;
  defaultClusterId?: string;
  defaultClusterName?: string;
}

export function StrategyView() {
  const navigate = useNavigate();
  const { clientUid, personaId } = useCreativeWorkbench();

  const {
    briefs,
    briefsLoading,
    briefsError,
    briefFilters,
    fetchBriefs,
    setBriefFilters,
  } = useGenerationStore();

  // Workbench context uses null for "unset"; component props use '' for "unset".
  const contextClientUid = clientUid ?? '';
  const contextPersonaId = personaId ?? '';

  const [modalOpen, setModalOpen] = useState(false);
  const [modalDefaults, setModalDefaults] = useState<ModalDefaults>({});

  // Persona name lookup so the rail/hero can show friendly labels.
  const [personaNames, setPersonaNames] = useState<Record<string, string>>({});
  const [clientNames, setClientNames] = useState<Record<string, string>>({});

  // Keep the generation store's filters in sync with Workbench context.
  // One-way: Workbench → store. The Briefs tab does the same sync, so both
  // tabs see a consistent filtered view when switching.
  // `null` sentinel ensures the first mount always syncs — even when context is empty —
  // to overwrite any filter state left behind by another tab.
  const lastSync = useRef<{ client: string | null; persona: string | null }>({
    client: null,
    persona: null,
  });
  useEffect(() => {
    if (
      lastSync.current.client === contextClientUid &&
      lastSync.current.persona === contextPersonaId
    ) {
      return;
    }
    lastSync.current = { client: contextClientUid, persona: contextPersonaId };
    setBriefFilters({
      client_uid: contextClientUid,
      persona_id: contextPersonaId,
    });
  }, [contextClientUid, contextPersonaId, setBriefFilters]);

  // Fetch briefs on mount and whenever the client/persona filter changes.
  useEffect(() => {
    fetchBriefs();
  }, [
    fetchBriefs,
    briefFilters.client_uid,
    briefFilters.persona_id,
    briefFilters.status,
  ]);

  // Resolve client display names for the suggestion rail header.
  useEffect(() => {
    let cancelled = false;
    personasApiV2
      .listClients()
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const c of res.clients) {
          map[c.client_uid] = c.client_name || c.client_uid;
        }
        setClientNames(map);
      })
      .catch(() => {
        /* header falls back to UID — non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve persona names for the currently-selected client.
  useEffect(() => {
    if (!contextClientUid) {
      setPersonaNames({});
      return;
    }
    let cancelled = false;
    personasApiV2
      .listPersonas(contextClientUid)
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const p of res.personas ?? []) {
          const id = p.id || p.slug || '';
          if (id) map[id] = p.name || id;
        }
        setPersonaNames(map);
      })
      .catch(() => {
        if (!cancelled) setPersonaNames({});
      });
    return () => {
      cancelled = true;
    };
  }, [contextClientUid]);

  const personaLabel = useMemo(
    () => (contextPersonaId ? personaNames[contextPersonaId] : undefined),
    [personaNames, contextPersonaId],
  );
  const clientLabel = useMemo(
    () => (contextClientUid ? clientNames[contextClientUid] : undefined),
    [clientNames, contextClientUid],
  );

  const handleBriefCreated = (briefId: string) => {
    // Refresh library in the background so the new brief shows up on return.
    fetchBriefs();
    navigate(`/creative/briefs/${briefId}`);
  };

  const openCreateModal = (defaults?: ModalDefaults) => {
    setModalDefaults(defaults ?? {});
    setModalOpen(true);
  };

  const handleAddBriefToLane = (campaignType: string, clusterId?: string) => {
    openCreateModal({
      defaultCampaignType: campaignType,
      defaultClientUid: contextClientUid || undefined,
      defaultPersonaId: contextPersonaId || undefined,
      defaultClusterId: clusterId,
    });
  };

  return (
    <div className="space-y-6">
      {/* Zone 1 — Persona-aligned performance hero (chart + KPIs).
          The freeform brief / chat experience moved to its own page. */}
      <StrategyPerformanceHero />

      {/* Zone 2 — Suggested Creative Ideas */}
      <BriefSuggestionRail
        contextClientUid={contextClientUid}
        contextPersonaId={contextPersonaId}
        personaLabel={personaLabel}
        clientLabel={clientLabel}
        onBriefCreated={handleBriefCreated}
        onOpenModal={() =>
          openCreateModal({
            defaultClientUid: contextClientUid || undefined,
            defaultPersonaId: contextPersonaId || undefined,
          })
        }
      />

      {/* Zone 3 — Playbook Library */}
      {briefsError && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium">Could not load briefs.</div>
            <div className="text-xs text-rose-600">{briefsError}</div>
          </div>
          <button
            type="button"
            onClick={() => fetchBriefs()}
            className="text-xs font-medium text-rose-700 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      <PlaybookLibrary
        briefs={briefs}
        briefsLoading={briefsLoading}
        contextClientUid={contextClientUid}
        contextPersonaId={contextPersonaId}
        onOpenBrief={(id) => navigate(`/creative/briefs/${id}`)}
        onAddBrief={handleAddBriefToLane}
        onStartFirstBrief={() =>
          openCreateModal({
            defaultClientUid: contextClientUid || undefined,
            defaultPersonaId: contextPersonaId || undefined,
          })
        }
      />

      {/* Phase 1 — unified BriefCreatorOverlay (replaces CreateBriefModal) */}
      <BriefCreatorOverlay
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onBriefCreated={(briefId) => {
          setModalOpen(false);
          handleBriefCreated(briefId);
        }}
        defaultClientUid={modalDefaults.defaultClientUid}
        defaultPersonaId={modalDefaults.defaultPersonaId}
        defaultClusterId={modalDefaults.defaultClusterId}
      />
    </div>
  );
}
